import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { cn } from '../../lib/utils';
import { List, Video, Clock } from 'lucide-react';
import DotGrid from '../../components/DotGrid';
import { usePriorityAccessModal } from '../../contexts/priority-access-modal-context';
import Analytics from '../../lib/analytics';

interface GettingStartedSectionProps {
  className?: string;
}

const steps = [
  {
    icon: List,
    title: 'Hop on the Priority Access List',
  },
  {
    icon: Video,
    title: 'Quick 5-minute demo',
  },
  {
    icon: Clock,
    title: '25-minute onboarding',
  },
];

export const GettingStartedSection: React.FC<GettingStartedSectionProps> = ({
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: '-20%' });
  const { openModal } = usePriorityAccessModal();

  // Handle CTA click with analytics
  const handleCtaClick = () => {
    Analytics.trackEvent('getting_started_cta_click', {
      event_category: 'Getting Started',
      event_label: 'Get Priority Access',
      trigger_location: 'getting-started',
    });
    openModal();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.25, 0, 1],
      },
    },
  };

  return (
    <motion.section
      id="getting-started"
      ref={containerRef}
      className={cn(
        'relative overflow-hidden bg-white py-8 md:py-12 lg:py-16',
        className
      )}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      {/* Interactive DotGrid Background */}
      <div className="absolute inset-x-0 -inset-y-6 overflow-hidden">
        <DotGrid
          dotSize={4}
          gap={24}
          baseColor="#94a3b8"
          activeColor="#64748b"
          proximity={80}
          shockRadius={150}
          shockStrength={2}
          resistance={800}
          returnDuration={1.5}
          className="opacity-35"
        />
      </div>

      {/* Subtle gradient overlay for depth */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-blue-100/15"></div>
      <div className="relative z-10 flex items-center justify-center px-4 md:px-10 lg:px-20">
        <motion.div
          className="layout-content-container flex max-w-3xl flex-col rounded-2xl bg-white p-8 shadow-2xl shadow-slate-900/25 md:p-12"
          variants={itemVariants}
        >
          <motion.h1
            className="text-center text-3xl leading-tight font-bold text-gray-900 md:text-4xl"
            variants={itemVariants}
          >
            Claim Priority Access
          </motion.h1>
          <motion.p
            className="pb-3 text-center text-base font-normal text-gray-400"
            variants={itemVariants}
          >
            (Limited spots)
          </motion.p>
          <motion.p
            className="text-center text-lg leading-relaxed font-normal text-gray-600"
            variants={itemVariants}
          >
            Be the leader who spots hidden growth opportunities today!
          </motion.p>
          <motion.p
            className="text-center text-lg leading-relaxed font-normal text-gray-600"
            variants={itemVariants}
          >
            Uncover profit leaks, stabilise cashflow, and sell smarter.
          </motion.p>

          <motion.div
            className="mt-10 border-t border-gray-200 pt-8"
            variants={itemVariants}
          >
            <motion.h3
              className="pb-6 text-center text-xl leading-tight font-bold tracking-[-0.015em] text-gray-800"
              variants={itemVariants}
            >
              Onboarding Process
            </motion.h3>
            <div className="space-y-6">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <motion.div
                    key={index}
                    className="flex items-center gap-4"
                    variants={itemVariants}
                  >
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                      <IconComponent size={24} />
                    </div>
                    <p className="flex-1 text-lg leading-normal font-medium text-gray-700">
                      {step.title}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            className="mt-8 flex justify-center px-4 py-3"
            variants={itemVariants}
          >
            <button
              onClick={handleCtaClick}
              className="flex h-12 w-full max-w-[480px] min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-blue-600 px-5 text-lg leading-normal font-bold tracking-[0.015em] text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-lg"
            >
              <span className="truncate">I want Priority Access</span>
            </button>
          </motion.div>

          <motion.p
            className="mt-2 text-center text-lg leading-relaxed font-medium text-green-600"
            variants={itemVariants}
          >
            100% cashback if cancelled during first month.
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
};
